import React, { useState, useEffect } from 'react';
import { userAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../styles/UserManagement.css';

const emptyForm = {
  username: '',
  full_name: '',
  email: '',
  password: '',
  role: 'user',
  permissions: []
};

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [permModal, setPermModal] = useState(null);   // user đang sửa quyền
  const [permDraft, setPermDraft] = useState([]);

  const [pwModal, setPwModal] = useState(null);        // user đang reset mật khẩu
  const [newPassword, setNewPassword] = useState('');

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadModules();
    loadUsers();
  }, []);

  const loadModules = async () => {
    try {
      const res = await userAPI.getModules();
      if (res.data.success) setModules(res.data.data);
    } catch (e) {
      console.error(e);
    }
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await userAPI.getAll();
      if (res.data.success) setUsers(res.data.data);
    } catch (e) {
      toast.error('Không tải được danh sách người dùng');
    } finally {
      setLoading(false);
    }
  };

  // ---------- Tạo user ----------
  const toggleFormPerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((p) => p !== key)
        : [...f.permissions, key]
    }));
  };

  const handleCreate = async () => {
    if (!form.username || !form.password) {
      toast.warning('Vui lòng nhập tên đăng nhập và mật khẩu');
      return;
    }
    try {
      const res = await userAPI.create(form);
      if (res.data.success) {
        toast.success('Tạo người dùng thành công');
        setShowCreate(false);
        setForm(emptyForm);
        loadUsers();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Tạo người dùng thất bại');
    }
  };

  // ---------- Phân quyền ----------
  const openPermModal = (user) => {
    setPermModal(user);
    setPermDraft(user.permissions || []);
  };

  const togglePermDraft = (key) => {
    setPermDraft((d) =>
      d.includes(key) ? d.filter((p) => p !== key) : [...d, key]
    );
  };

  const savePermissions = async () => {
    try {
      const res = await userAPI.updatePermissions(permModal.id, permDraft);
      if (res.data.success) {
        toast.success('Cập nhật quyền thành công');
        setPermModal(null);
        loadUsers();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Cập nhật quyền thất bại');
    }
  };

  // ---------- Khoá / mở khoá ----------
  const handleToggleActive = async (user) => {
    try {
      const res = await userAPI.toggleActive(user.id);
      if (res.data.success) {
        toast.success(res.data.message);
        loadUsers();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Thao tác thất bại');
    }
  };

  // ---------- Reset mật khẩu ----------
  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.warning('Mật khẩu tối thiểu 6 ký tự');
      return;
    }
    try {
      const res = await userAPI.resetPassword(pwModal.id, newPassword);
      if (res.data.success) {
        toast.success('Đặt lại mật khẩu thành công');
        setPwModal(null);
        setNewPassword('');
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Đặt lại mật khẩu thất bại');
    }
  };

  // ---------- Xoá ----------
  const handleDelete = async (user) => {
    if (!window.confirm(`Xoá người dùng "${user.username}"?`)) return;
    try {
      const res = await userAPI.delete(user.id);
      if (res.data.success) {
        toast.success('Đã xoá người dùng');
        loadUsers();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Xoá thất bại');
    }
  };

  const moduleLabel = (key) => modules.find((m) => m.key === key)?.label || key;

  if (loading) {
    return <div className="um-container"><div className="loading">Đang tải...</div></div>;
  }

  return (
    <div className="um-container">
      <div className="page-header um-header">
        <div>
          <h1>Quản lý User</h1>
          <p>Thêm người dùng và phân quyền theo từng module</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ Thêm người dùng</button>
      </div>

      <div className="card">
        <table className="um-table">
          <thead>
            <tr>
              <th>Người dùng</th>
              <th>Vai trò</th>
              <th>Quyền truy cập</th>
              <th>Trạng thái</th>
              <th>Đăng nhập gần nhất</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="um-user">
                    <strong>{u.full_name || u.username}</strong>
                    <span className="um-sub">@{u.username}{u.email ? ` · ${u.email}` : ''}</span>
                  </div>
                </td>
                <td>
                  <span className={`um-badge ${u.role === 'admin' ? 'badge-admin' : 'badge-user'}`}>
                    {u.role === 'admin' ? 'Admin' : 'User'}
                  </span>
                </td>
                <td>
                  {u.role === 'admin' ? (
                    <span className="um-all">Toàn quyền</span>
                  ) : (
                    <div className="um-perms">
                      {(u.permissions || []).length === 0
                        ? <span className="um-sub">Chưa có quyền</span>
                        : u.permissions.map((p) => (
                            <span key={p} className="um-chip">{moduleLabel(p)}</span>
                          ))}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`um-dot ${u.is_active ? 'on' : 'off'}`} />
                  {u.is_active ? 'Hoạt động' : 'Đã khoá'}
                </td>
                <td className="um-sub">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleString('vi-VN') : '—'}
                </td>
                <td>
                  <div className="um-actions">
                    {u.role !== 'admin' && (
                      <button className="btn-link" onClick={() => openPermModal(u)}>Phân quyền</button>
                    )}
                    <button className="btn-link" onClick={() => setPwModal(u)}>Đổi MK</button>
                    {u.id !== currentUser.id && (
                      <>
                        <button className="btn-link" onClick={() => handleToggleActive(u)}>
                          {u.is_active ? 'Khoá' : 'Mở'}
                        </button>
                        <button className="btn-link danger" onClick={() => handleDelete(u)}>Xoá</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal tạo user */}
      {showCreate && (
        <div className="um-modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Thêm người dùng</h3>
            <div className="um-grid">
              <div className="form-group">
                <label>Tên đăng nhập *</label>
                <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Họ tên</label>
                <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Mật khẩu *</label>
                <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Vai trò</label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="user">User</option>
                  <option value="admin">Admin (toàn quyền)</option>
                </select>
              </div>
            </div>

            {form.role !== 'admin' && (
              <div className="um-perm-box">
                <label className="um-perm-title">Quyền truy cập module</label>
                <div className="um-perm-grid">
                  {modules.map((m) => (
                    <label key={m.key} className="um-check">
                      <input
                        type="checkbox"
                        checked={form.permissions.includes(m.key)}
                        onChange={() => toggleFormPerm(m.key)}
                      />
                      <span>{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="um-modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreate(false)}>Huỷ</button>
              <button className="btn-primary" onClick={handleCreate}>Tạo</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal phân quyền */}
      {permModal && (
        <div className="um-modal-overlay" onClick={() => setPermModal(null)}>
          <div className="um-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Phân quyền: {permModal.full_name || permModal.username}</h3>
            <div className="um-perm-grid">
              {modules.map((m) => (
                <label key={m.key} className="um-check">
                  <input
                    type="checkbox"
                    checked={permDraft.includes(m.key)}
                    onChange={() => togglePermDraft(m.key)}
                  />
                  <span>{m.label}</span>
                </label>
              ))}
            </div>
            <div className="um-modal-actions">
              <button className="btn-secondary" onClick={() => setPermModal(null)}>Huỷ</button>
              <button className="btn-primary" onClick={savePermissions}>Lưu quyền</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal reset mật khẩu */}
      {pwModal && (
        <div className="um-modal-overlay" onClick={() => setPwModal(null)}>
          <div className="um-modal um-modal-sm" onClick={(e) => e.stopPropagation()}>
            <h3>Đổi mật khẩu: {pwModal.username}</h3>
            <div className="form-group">
              <label>Mật khẩu mới</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Tối thiểu 6 ký tự"
              />
            </div>
            <div className="um-modal-actions">
              <button className="btn-secondary" onClick={() => { setPwModal(null); setNewPassword(''); }}>Huỷ</button>
              <button className="btn-primary" onClick={handleResetPassword}>Lưu</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
